import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { AuthModule, OidcSecurityService } from 'angular-auth-oidc-client';
import { authConfig } from '../utils/auth.config';
import { provideTranslateService } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeGermany from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeGermanyExtra from '@angular/common/locales/extra/de';
import localeEnglishExtra from '@angular/common/locales/extra/en';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { provideRouter, withHashLocation } from '@angular/router';
import { routes } from './app-routing';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAppTranslationLoader } from './shared/utils/translation-loader';
import { AuthInterceptorService } from './shared/services/auth-interceptor.service';
import { AuthService } from './shared/services/auth.service';
import { PrimeNGConfigType, providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { provideServiceWorker } from "@angular/service-worker";
import { MessageService } from 'primeng/api';

export function authInitializer( oidc: OidcSecurityService ): () => Promise<void> {
  return async () => {
    const params = new URLSearchParams( window.location.search );
    const error = params.get( 'error' );
    const errorDesc = params.get( 'error_description' );

    // ✅ 1. Handle Cognito errors
    if( error || errorDesc ) {
      let message = decodeURIComponent( errorDesc || error );
      message = message.replace( /\+/g, ' ' ).trim();

      const knownPrefixes = [
        'Invalid SAML response received',
        'PostAuthentication failed with error',
        'invalid_request',
        'unauthorized_client',
        'access_denied',
        'server_error',
      ];

      for( const prefix of knownPrefixes ) {
        const regex = new RegExp( `^${prefix}[:\\-]?\\s*`, 'i' );
        message = message.replace( regex, '' );
      }

      if( !message || message.length < 3 ) {
        message = 'An authentication error occurred. Please try signing in again.';
      }

      // ✅ Replace NgZorro with PrimeNG
      const messageService = inject( MessageService );
      messageService.add( {
        severity: 'error',
        summary: 'Authentication Error',
        detail: message,
        life: 5000,
      } );

      window.history.replaceState( {}, document.title, window.location.pathname );

      setTimeout( () => {
        oidc.authorize();
      }, 5000 );

      return;
    }

    // ✅ 2. Handle "code but no state"
    const hasCode = params.has( 'code' );
    const hasState = params.has( 'state' );

    if( hasCode && !hasState ) {
      window.history.replaceState( {}, document.title, window.location.origin );
      oidc.authorize();
      return;
    }

    // ✅ 3. Check authentication normally
    const { isAuthenticated } = await firstValueFrom( oidc.checkAuth() );
    if( !isAuthenticated ) {
      oidc.authorize();
    }
  };
}

export function initializeAuth(): Promise<void> {
  const initializerFn = authInitializer( inject( OidcSecurityService ) );

  return initializerFn();
}

registerLocaleData( localeGermany, 'de-DE', localeGermanyExtra );
registerLocaleData( localeEn, 'en-EN', localeEnglishExtra );

const primeNgConfig: PrimeNGConfigType = {
  ripple: true,
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
      cssLayer: {
        name: 'primeng',
        order: 'tailwind-base, primeng, tailwind-utilities'
      }
    }
  },
}

export const appConfig: ApplicationConfig = {
  providers: [
    AuthService,
    MessageService,
    provideAnimations(),
    provideZoneChangeDetection(),
    providePrimeNG( primeNgConfig ),
    provideRouter( routes, withHashLocation() ),
    provideHttpClient( withInterceptorsFromDi() ),
    provideTranslateService( {
      loader: provideAppTranslationLoader(),
    } ),
    importProvidersFrom( [
      AuthModule.forRoot( { config: authConfig } )
    ] ),
    provideAppInitializer( initializeAuth ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    },
    {
      provide: NZ_I18N,
      useValue: en_US
    },
    {
      provide: LOCALE_ID,
      useValue: 'en-GB' // 'de-DE' for Germany, 'fr-FR' for France ...
    },
    provideServiceWorker( 'ngsw-worker.js', {
      enabled: !isDevMode()
    } )
  ]
}
